import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Input } from '../../../../components/ui/input';
import { CriticalStockProduct } from '../../../../lib/types';
import { ArrowLeft, Search, Filter } from 'lucide-react';
import { useAuth } from '../../../../hooks/useAuth';

export function AllCriticalProducts() {
  const { brandId: urlBrandId } = useParams<{ brandId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<CriticalStockProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMachine, setSelectedMachine] = useState<string>('');
  const [machines, setMachines] = useState<Array<{ id: string; name: string }>>([]);

  // Use brandId from URL params, fallback to user's tenantId
  const brandId = urlBrandId || user?.tenantId;

  // Debug logging
  console.log('AllCriticalProducts - brandId from params:', urlBrandId);
  console.log('AllCriticalProducts - user tenantId:', user?.tenantId);
  console.log('AllCriticalProducts - final brandId:', brandId);
  console.log('AllCriticalProducts - current URL:', window.location.href);

  useEffect(() => {
    if (brandId) {
      fetchAllProducts();
    }
  }, [brandId]);

  const fetchAllProducts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/brand/${brandId}/dashboard/stock/products/all`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch all critical products');
      }

      const data = await response.json();
      setData(data);
      
      // Also fetch machines for the filter
      const machinesResponse = await fetch(`/api/brand/${brandId}/dashboard/stock`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (machinesResponse.ok) {
        const stockData = await machinesResponse.json();
        setMachines(stockData.machines || []);
      }
    } catch (error) {
      console.error('Error fetching all critical products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/brand/${brandId}/dashboard/stock`);
  };

  // Filter data based on search term and selected machine
  const filteredData = data.filter(product => {
    const matchesSearch = product.productName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesMachine = selectedMachine === 'all' || !selectedMachine || product.productName.toLowerCase().includes(selectedMachine.toLowerCase());
    return matchesSearch && matchesMachine;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="outline" size="sm" onClick={handleBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Stock Reporting
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">All Critical Stock Products</h1>
            <p className="text-gray-600">{filteredData.length} products with critical stock levels</p>
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder="Search by product name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="sm:w-64">
              <Select value={selectedMachine} onValueChange={setSelectedMachine}>
                <SelectTrigger>
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Filter by machine" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Machines</SelectItem>
                  {machines.map((machine) => (
                    <SelectItem key={machine.id} value={machine.name}>
                      {machine.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product Name
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    No. Of Machines with Critical
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Impact Level
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredData.map((product) => {
                  const impactLevel = product.machinesWithCritical > 5 ? 'High' : 
                                    product.machinesWithCritical > 2 ? 'Medium' : 'Low';
                  const impactColor = product.machinesWithCritical > 5 ? 'bg-red-100 text-red-800' : 
                                    product.machinesWithCritical > 2 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800';
                  const status = product.machinesWithCritical > 5 ? 'Action Required' : 
                               product.machinesWithCritical > 2 ? 'Monitor Closely' : 'Under Control';
                  
                  return (
                    <tr key={product.productId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {product.productName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                        <span className={`font-medium ${
                          product.machinesWithCritical > 5 ? 'text-red-600' : 
                          product.machinesWithCritical > 2 ? 'text-yellow-600' : 'text-green-600'
                        }`}>
                          {product.machinesWithCritical}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${impactColor}`}>
                          {impactLevel}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`text-sm font-medium ${
                          product.machinesWithCritical > 5 ? 'text-red-600' : 
                          product.machinesWithCritical > 2 ? 'text-yellow-600' : 'text-green-600'
                        }`}>
                          {status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
